import { ChangeDetectionStrategy, Component, WritableSignal, computed, input, linkedSignal, output } from '@angular/core';
import moment from 'moment';
import { AnimationOptions } from 'ngx-lottie';
import { IBase, IVehicleScheduleAvailability, IVehicleScheduleConflict, IVehicleScheduleEdit, IVehicleScheduleTrip } from 'src/app/models';
import { InputType } from 'src/app/models/enums';
import { LiteralService, UtilsService } from 'src/app/services';
import { images } from 'src/images';
import { editedAvailabilitySlot, fitsAvailability, scheduleServiceLabel, withTime } from '../scheduleChanges';

@Component({
  selector: 'app-vehicle-schedule-edit-modal',
  templateUrl: './vehicle-schedule-edit-modal.component.html',
  styleUrl: './vehicle-schedule-edit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehicleScheduleEditModalComponent {
  readonly clientIds = input<number[]>([]);
  readonly availability = input.required<IVehicleScheduleAvailability>();
  readonly trip = input<IVehicleScheduleTrip>();
  readonly findAvailability = input<(edit: IVehicleScheduleEdit) => IVehicleScheduleAvailability | undefined>();
  readonly conflicts = input<IVehicleScheduleConflict[]>([]);
  readonly conflictsOnly = input(false);
  readonly checking = input(false);
  readonly proposedEdit = input<IVehicleScheduleEdit>();
  readonly saveEventEmitter = output<IVehicleScheduleEdit>();
  readonly assignmentChangeEventEmitter = output<IVehicleScheduleEdit>();
  readonly closeEventEmitter = output<void>();
  readonly confirmConflictsEventEmitter = output<void>();
  readonly cancelConflictsEventEmitter = output<void>();

  public images = images;
  public loadingAnimation: AnimationOptions = {
    path: '/assets/animations/loading-login-green.json',
  };

  isTrip = computed(() => !!this.trip());
  openedByClick = computed(() => !this.conflictsOnly() && !this.proposedEdit());
  private edited = computed(() => this.trip() ?? this.availability());

  serviceLabel = computed(() => scheduleServiceLabel(this.availability().service));
  routeName = computed(() => (this.trip() ?? this.availability()).route?.name ?? '');

  driver = linkedSignal(() => (this.proposedEdit() ? this.proposedEdit()!.driver : this.edited().driver));
  vehicle = linkedSignal(() => (this.proposedEdit() ? this.proposedEdit()!.vehicle : this.edited().vehicle));
  driverOptions = computed(() => this.assignedOption(this.edited().driver));
  vehicleOptions = computed(() => this.assignedOption(this.edited().vehicle));
  startTime = linkedSignal(() => this.proposedEdit()?.startTime ?? this.formatTime(this.edited().startDateTime));
  endTime = linkedSignal(() => this.proposedEdit()?.endTime ?? this.formatTime(this.edited().endDateTime));
  assignmentChanged = computed(
    () => !this.isTrip() && (this.driver()?.id !== this.availability().driver?.id || this.vehicle()?.id !== this.availability().vehicle?.id),
  );
  assignmentStep = linkedSignal(() => !!this.proposedEdit());
  applyToTrips = linkedSignal(() => {
    this.edited();
    return true;
  });
  locked = computed(() => !!this.conflicts().length || this.assignmentStep());

  startTimeInput = computed(() => this.timeInput(this.startTime()));
  endTimeInput = computed(() => this.timeInput(this.endTime()));
  validTimes = computed(
    () => withTime(this.edited().endDateTime, this.endTime()) > withTime(this.edited().startDateTime, this.startTime()),
  );
  edit = computed<IVehicleScheduleEdit>(() => ({
    availabilityId: this.availability().id,
    tripId: this.trip()?.id,
    driver: this.driver(),
    vehicle: this.vehicle(),
    startTime: this.startTime(),
    endTime: this.endTime(),
    shiftMinutes: this.proposedEdit()?.shiftMinutes,
    applyToTrips: this.assignmentChanged() ? this.applyToTrips() : undefined,
  }));
  tripFits = computed(() => {
    const findAvailability = this.findAvailability();
    return !this.isTrip() || !findAvailability || !!findAvailability(this.edit());
  });
  tripsInside = computed(() => {
    if (this.isTrip()) {
      return true;
    }
    const edited = editedAvailabilitySlot(this.availability(), this.edit());
    return edited.trips.every((trip) => fitsAvailability(edited, trip));
  });
  canSubmit = computed(() => this.validTimes() && this.tripFits() && this.tripsInside() && !this.checking());

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
  ) {}

  private assignedOption(assigned?: IBase): { value: number; label: string }[] {
    return assigned?.id ? [{ value: assigned.id, label: assigned.name ?? '' }] : [];
  }

  timesLabel(trip?: IVehicleScheduleTrip): string {
    if (trip) {
      return `${this.formatTime(trip.startDateTime)} - ${this.formatTime(trip.endDateTime)}`;
    }
    return `${this.startTime()} - ${this.endTime()}`;
  }

  private formatTime(date: Date): string {
    return moment(date).format('HH:mm');
  }

  private timeInput(value: string) {
    return { height: 32, fontSize: 16, value, edit: { inputType: InputType.TIME } };
  }

  private assign(assigned: WritableSignal<IBase | undefined>, selected: { value: number; label: string }) {
    if (selected.value && selected.value !== assigned()?.id) {
      assigned.set({ id: selected.value, name: selected.label });
      this.clearConflicts();
      if (this.isTrip()) {
        this.assignmentChangeEventEmitter.emit(this.edit());
      }
    }
  }

  changeDriver(selected: { value: number; label: string }) {
    this.assign(this.driver, selected);
  }

  changeVehicle(selected: { value: number; label: string }) {
    this.assign(this.vehicle, selected);
  }

  changeStartTime(event: Event) {
    this.startTime.set((event.target as HTMLInputElement).value);
    this.clearConflicts();
  }

  changeEndTime(event: Event) {
    this.endTime.set((event.target as HTMLInputElement).value);
    this.clearConflicts();
  }

  close() {
    this.closeEventEmitter.emit();
  }

  confirmConflicts() {
    this.confirmConflictsEventEmitter.emit();
  }

  cancelConflicts() {
    this.cancelConflictsEventEmitter.emit();
  }

  private clearConflicts() {
    if (this.conflicts().length) {
      this.cancelConflicts();
    }
  }

  goToTrip() {
    window.open(`/trips/${this.trip()?.id}`, '_blank');
  }

  changeApplyToTrips(event: Event) {
    this.applyToTrips.set((event.target as HTMLInputElement).checked);
  }

  cancelForm() {
    if (this.assignmentStep()) {
      this.cancelAssignment();
    } else {
      this.close();
    }
  }

  cancelAssignment() {
    if (this.proposedEdit()) {
      this.close();
    } else {
      this.assignmentStep.set(false);
    }
  }

  onSubmit() {
    if (!this.canSubmit()) {
      return;
    }
    if (this.assignmentChanged() && this.availability().trips.length && !this.assignmentStep()) {
      this.assignmentStep.set(true);
      return;
    }
    this.saveEventEmitter.emit(this.edit());
  }
}
